import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { UsersThree, TrendUp, CurrencyInr, Target, Receipt, FileText, ArrowLeft, ArrowRight, Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { ViewToggle, useViewMode } from '../components/ViewToggle';
import { PowerTable } from '../components/PowerTable';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import useAuthStore from '../store/authStore';
import { confirm } from '../components/ConfirmDialog';
import DeleteUserModal from '../components/DeleteUserModal';
import SalaryCard from '../components/SalaryCard';
import PipelineToolbar from '../components/PipelineToolbar';
import { sortRows } from '../lib/pipelineFilters';

import { notify } from '../lib/notify';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_COLOR = {
  'Regional Sales Manager': 'bg-blue-100 text-blue-700',
  'National Sales Manager': 'bg-purple-100 text-purple-700',
  'Vice President': 'bg-pink-100 text-pink-700',
  'Sales Executive': 'bg-emerald-100 text-emerald-700',
  'Sales Coordinator': 'bg-amber-100 text-amber-700',
  'Service Executive': 'bg-teal-100 text-teal-700',
  'Tender Executive': 'bg-indigo-100 text-indigo-700',
};

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const ALL_ROLES = [
  'Sales Executive',
  'Service Executive',
  'Sales Coordinator',
  'Tender Executive',                    // iter89 — TE role
  'Regional Sales Manager',
  'National Sales Manager',
  'Vice President',
];
const ALL_REGIONS = ['North', 'South', 'East', 'West', 'Central'];

const emptyMember = {
  monthly_salary: '',
  name: '',
  email: '',
  role: 'Sales Executive',
  region: '',
  employee_id: '',
  phone: '',
  aadhar_number: '',
  date_of_joining: '',
  password: '',
};

const Team = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Review reliability rollup — populated in parallel with overview so the
  // extra column doesn't block the main table render. Keyed by user_id.
  const [reliability, setReliability] = useState({});
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all');
  const [viewMode, setViewMode] = useViewMode('team');
  // Team-member sort — pipeline value, approved value, or name.
  const [teamSortBy, setTeamSortBy] = useState('pipeline_desc');
  // CRUD state — President only
  const [modalMode, setModalMode] = useState(null); // 'add' | 'edit' | null
  const [memberForm, setMemberForm] = useState(emptyMember);
  const [memberFormBusy, setMemberFormBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isPresident = user?.role === 'President';

  const reload = async () => {
    try {
      const r = await axios.get(`${API}/team/overview`);
      setData(r.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/team/overview`);
        setData(r.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
    // Fetch review reliability alongside — silent on error since the feature
    // is optional (a tenant may have no closed cycles yet).
    axios.get(`${API}/reviews/team-performance`)
      .then((r) => {
        const map = {};
        for (const row of (r.data.performance || [])) map[row.user_id] = row;
        setReliability(map);
      })
      .catch(() => setReliability({}));
  }, []);

  // === President CRUD handlers ===
  const openAddModal = () => { setMemberForm(emptyMember); setModalMode('add'); };
  const openEditModal = (m) => {
    setMemberForm({
      ...emptyMember,
      _editing_id: m.user_id,
      name: m.name || '',
      email: m.email || '',
      role: m.role || 'Sales Executive',
      region: m.region || '',
      employee_id: m.employee_id || '',
      phone: m.phone || '',
    });
    setModalMode('edit');
  };

  const submitMember = async () => {
    if (!memberForm.name || !memberForm.email || !memberForm.role) {
      notify('Name, Email and Role are required'); return;
    }
    if (modalMode === 'add' && (!memberForm.employee_id || !memberForm.password)) {
      notify('Employee ID and initial Password are required'); return;
    }
    setMemberFormBusy(true);
    try {
      if (modalMode === 'add') {
        await axios.post(`${API}/team/members`, {
          name: memberForm.name,
          email: memberForm.email,
          role: memberForm.role,
          region: memberForm.region,
          employee_id: memberForm.employee_id,
          aadhar_number: memberForm.aadhar_number,
          date_of_joining: memberForm.date_of_joining,
          phone: memberForm.phone,
          password: memberForm.password,
          ...(memberForm.monthly_salary !== '' ? { monthly_salary: Number(memberForm.monthly_salary) } : {}),
        });
      } else {
        const patch = {
          name: memberForm.name,
          email: memberForm.email,
          role: memberForm.role,
          region: memberForm.region,
          employee_id: memberForm.employee_id,
          phone: memberForm.phone,
        };
        if (memberForm.password) patch.password = memberForm.password;
        await axios.patch(`${API}/team/members/${memberForm._editing_id}`, patch);
      }
      setModalMode(null);
      setMemberForm(emptyMember);
      await reload();
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to save member');
    } finally {
      setMemberFormBusy(false);
    }
  };

  const removeMember = (m) => {
    // Open reason-modal (required by backend; recorded in audit log)
    setDeleteTarget({ id: m.user_id, name: m.name, email: m.email, role: m.role });
  };

  const _ = confirm; // kept for other callers; unused here
  void _;

  const handleDeleteWithReason = async (reason) => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/team/members/${deleteTarget.id}`, { params: { reason } });
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const msg = !e.response
        ? `Network error: could not reach the server. ${e.message || ''}`
        : status === 401 || status === 403
          ? `Permission denied (${status}). ${detail || 'You may need to log out and back in.'}`
          : status === 404
            ? 'Member not found — they may already have been removed.'
            : `Failed to remove: ${detail || `HTTP ${status}`}`;
      const err = new Error(msg);
      err.response = e.response;
      throw err; // bubble back to modal so it shows the error
    }
    setDeleteTarget(null);
    try { await reload(); } catch (e) {
      console.error('[Team] Reload after delete failed', e);
      notify('Member was removed, but the list could not refresh. Please reload the page.');
    }
  };

  /* --------------------------------------------------------------------------
     Feb 2026 — President-only session controls. Both use `window.confirm` /
     `window.prompt` for now to keep the change small; if this UX starts
     getting used a lot we can swap to a nicer modal.
     -------------------------------------------------------------------------- */
  const handleForceLogout = async (m) => {
    const ok = await confirm({ message: 
      `Force logout ${m.name} (${m.email}) from all their devices?\n\n` +
      `They stay in the team and keep their password — but their current sessions die instantly. Next click sends them to the login page.`
     });
    if (!ok) return;
    try {
      await axios.post(`${API}/users/${m.user_id}/force-logout`);
      notify(`✅ Signed ${m.name} out of every device.`);
    } catch (e) {
      notify(`❌ Force logout failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const handleAdminResetPassword = async (m) => {
    const pw = window.prompt(
      `Reset password for ${m.name} (${m.email}).\n\n` +
      `Enter a new password (min 8 chars). Their existing sessions on all devices will also be signed out.`
    );
    if (pw === null) return;              // cancelled
    if (pw.trim().length < 8) {
      notify('Password must be at least 8 characters.');
      return;
    }
    const confirm2 = window.prompt(
      `Confirm the new password for ${m.name} by typing it again:`
    );
    if (confirm2 !== pw) {
      notify('Passwords did not match. No change was made.');
      return;
    }
    try {
      await axios.post(`${API}/users/${m.user_id}/reset-password`, { new_password: pw });
      notify(
        `✅ Password reset for ${m.name}.\n\nShare the new password with them via a safe channel (not this app). Their old sessions have been signed out.`
      );
    } catch (e) {
      notify(`❌ Reset failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const openMember = async (m) => {
    setSelectedMember(m);
    setDetailLoading(true);
    setMemberDetail(null);
    try {
      const r = await axios.get(`${API}/team/member/${m.user_id}`);
      setMemberDetail(r.data);
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to load member detail');
      setSelectedMember(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setSelectedMember(null); setMemberDetail(null); };

  if (loading) {
    return <div className="p-8 text-center text-[#6D8E87]">Loading team...</div>;
  }
  if (!data) {
    return <div className="p-8 text-center text-rose-700">Failed to load team data.</div>;
  }

  const summary = data.summary || {};
  const members = data.members || [];
  const roleFilteredMembers = roleFilter === 'all' ? members : members.filter(m => m.role === roleFilter);
  // Apply team-member sort. Map dropdown keys to sortRows options.
  const sortKey = teamSortBy === 'pipeline_desc' ? 'value_desc'
                : teamSortBy === 'approved_desc' ? 'value_desc'
                : teamSortBy;
  const sortValueKey = teamSortBy === 'approved_desc' ? 'approved_value' : 'pipeline_value';
  const visibleMembers = sortRows(roleFilteredMembers, sortKey, {
    valueKey: sortValueKey, dateKey: 'updated_at',
  });
  const availableRoles = Array.from(new Set(members.map(m => m.role)));

  // Detail view
  if (selectedMember && memberDetail) {
    const k = memberDetail.kpis;
    return (
      <div className="p-4 sm:p-6 lg:p-8" data-testid="team-member-detail">
        <button
          onClick={closeDetail}
          className="flex items-center gap-2 text-[#00a69c] mb-6 hover:underline"
          data-testid="back-to-team-btn"
        >
          <ArrowLeft size={18} /> Back to Team
        </button>

        {/* Header */}
        <div className="medical-card p-5 mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#E2F3F0] text-[#00a69c] flex items-center justify-center text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                {(memberDetail.user.name || '?').charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>
                  {memberDetail.user.name}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLOR[memberDetail.user.role] || 'bg-gray-100 text-gray-700'}`}>
                    {memberDetail.user.role}
                  </span>
                  {memberDetail.user.region && (
                    <span className="text-xs text-[#6D8E87]">• {memberDetail.user.region}</span>
                  )}
                  <span className="text-xs text-[#6D8E87]">• {memberDetail.user.email}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-[#6D8E87]">Performance Score</div>
              <div className="text-3xl font-bold text-[#00a69c]" style={{ fontFamily: 'Outfit' }}>
                {k.performance_score}<span className="text-base text-[#6D8E87]">/100</span>
              </div>
            </div>
          </div>
        </div>

        {['Vice President', 'President'].includes(user?.role) && <SalaryCard userId={memberDetail.user.id} />}

        {/* KPI Cards (clickable to route into respective sections) */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="member-kpi-grid">
          {[
            { label: 'Revenue Won', value: formatINR(k.revenue_won), color: '#00a69c', bg: '#E2F3F0', icon: CurrencyInr, route: '/leads' },
            { label: 'Total Leads', value: k.total_leads, color: '#3B82F6', bg: '#DBEAFE', icon: UsersThree, route: '/leads' },
            { label: 'Conversion', value: `${k.conversion_rate}%`, color: '#10B981', bg: '#D1FAE5', icon: TrendUp, route: null },
            { label: 'Quotations', value: `${k.quotes_approved}/${k.quotes_total}`, color: '#F59E0B', bg: '#FEF3C7', icon: FileText, route: '/quotations' },
            { label: 'PI Open / Converted', value: `${k.pis_open}/${k.pis_converted}`, color: '#8B5CF6', bg: '#EDE9FE', icon: Receipt, route: '/proforma-invoices' },
            { label: 'Goals Active', value: k.goals_count, color: '#EF4444', bg: '#FEE2E2', icon: Target, route: '/goals' },
          ].map((kpi, i) => (
            <button
              key={i}
              type="button"
              disabled={!kpi.route}
              onClick={() => kpi.route && navigate(kpi.route)}
              className={`medical-card p-3 text-left ${kpi.route ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all' : 'cursor-default'}`}
              data-testid={`member-kpi-${kpi.label.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: kpi.bg, color: kpi.color }}>
                  <kpi.icon size={14} weight="bold" />
                </div>
                <span className="text-[10px] text-[#6D8E87] uppercase tracking-wide">{kpi.label}</span>
              </div>
              <div className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>{kpi.value}</div>
            </button>
          ))}
        </div>

        {/* Lead & Quote sub-lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="medical-card p-5">
            <h3 className="text-base font-bold text-[#052A23] mb-3" style={{ fontFamily: 'Outfit' }}>
              Recent Leads ({k.total_leads})
            </h3>
            {memberDetail.leads.length === 0 ? (
              <p className="text-sm text-[#6D8E87] italic">No leads yet.</p>
            ) : (
              <div className="divide-y divide-[#D5E8E4] max-h-96 overflow-y-auto">
                {memberDetail.leads.slice(0, 10).map(l => (
                  <button
                    key={l.id}
                    onClick={() => navigate(`/leads/${l.id}`)}
                    className="w-full text-left py-2 px-1 hover:bg-[#F4F9F7] transition"
                    data-testid={`detail-lead-${l.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm text-[#052A23] truncate">{l.lead_name || '—'}</div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        l.status === 'won' ? 'bg-emerald-100 text-emerald-700' :
                        l.status === 'lost' ? 'bg-rose-100 text-rose-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{l.status?.toUpperCase()}</span>
                    </div>
                    <div className="text-xs text-[#6D8E87] mt-0.5">{l.customer_name || ''} • {l.region || ''}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="medical-card p-5">
            <h3 className="text-base font-bold text-[#052A23] mb-3" style={{ fontFamily: 'Outfit' }}>
              Recent Quotations ({k.quotes_total})
            </h3>
            {memberDetail.quotations.length === 0 ? (
              <p className="text-sm text-[#6D8E87] italic">No quotations yet.</p>
            ) : (
              <div className="divide-y divide-[#D5E8E4] max-h-96 overflow-y-auto">
                {memberDetail.quotations.slice(0, 10).map(q => (
                  <button
                    key={q.id}
                    onClick={() => navigate(`/quotations/${q.id}`)}
                    className="w-full text-left py-2 px-1 hover:bg-[#F4F9F7] transition"
                    data-testid={`detail-quote-${q.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm text-[#052A23] truncate">{q.quotation_number || q.id.slice(0, 8)}</div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        q.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        q.status === 'pending_approval' ? 'bg-amber-100 text-amber-700' :
                        q.status === 'rejected' ? 'bg-rose-100 text-rose-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{(q.status || '').replace('_', ' ').toUpperCase()}</span>
                    </div>
                    <div className="text-xs text-[#6D8E87] mt-0.5">
                      {q.lead_name || ''} • {formatINR(q.total_amount || q.total || 0)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Team list view
  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="team-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#E2F3F0] text-[#00a69c] flex items-center justify-center">
            <UsersThree size={28} weight="bold" />
          </div>
          <div>
            <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>
              My Team
            </h1>
            <p className="text-[#6D8E87]">
              {user?.role === 'Regional Sales Manager' ? 'Sales executives in your region' :
                user?.role === 'National Sales Manager' ? 'All RSMs and their teams across regions' :
                user?.role === 'Vice President' ? 'National sales managers, RSMs and field reps' :
                'Entire sales organisation'}
            </p>
          </div>
        </div>
        {isPresident && (
          <div className="flex gap-2">
            <button
              onClick={async () => {
                if (!await confirm({ message: 'Repair users missing tenant_id? Idempotent — safe to run multiple times.' })) return;
                try {
                  const r = await axios.post(`${API}/admin/team/backfill-tenant-id`);
                  notify(r.data.message || 'Done');
                  reload();
                } catch (e) {
                  notify(e.response?.data?.detail || 'Repair failed');
                }
              }}
              className="flex items-center gap-2 px-3 py-2 border border-amber-300 bg-amber-50 text-amber-800 rounded-lg text-xs font-semibold hover:bg-amber-100"
              title="Heal any user row created without a tenant_id (iter90 legacy bug)."
              data-testid="repair-orphan-users-btn"
            >
              🩹 Repair orphan users
            </button>
            <button
              onClick={openAddModal}
              className="flex items-center gap-2 px-4 py-2 bg-[#00a69c] text-white rounded-lg font-medium hover:bg-[#008f86] transition"
              data-testid="add-member-btn"
            >
              <Plus size={18} weight="bold" /> Add Team Member
            </button>
          </div>
        )}
      </div>

      {/* Summary Strip — richer rollup added Feb 2026: pipeline/quoted/approved
          give managers a leading indicator of team performance BEFORE revenue is
          realised, complementing the trailing `Revenue Won` figure. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3" data-testid="team-summary">
        <div className="medical-card p-4 border-l-4 border-l-[#00a69c]">
          <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold">Team Size</div>
          <div className="text-2xl md:text-[1.6rem] font-black text-[#052A23]" style={{ fontFamily: 'Outfit' }}>{summary.team_size || 0}</div>
          <div className="text-[11px] text-[#6D8E87] mt-1">{summary.total_active_leads || 0} active leads</div>
        </div>
        <div className="medical-card p-4 border-l-4 border-l-slate-400" data-testid="team-summary-pipeline">
          <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold">Pipeline Value</div>
          <div className="text-2xl md:text-[1.6rem] font-black text-[#052A23]" style={{ fontFamily: 'Outfit' }}>{formatINR(summary.total_pipeline_value || 0)}</div>
          <div className="text-[11px] text-[#6D8E87] mt-1">team's open opportunity</div>
        </div>
        <div className="medical-card p-4 border-l-4 border-l-amber-500" data-testid="team-summary-quoted">
          <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold">Quoted Value</div>
          <div className="text-2xl md:text-[1.6rem] font-black text-[#052A23]" style={{ fontFamily: 'Outfit' }}>{formatINR(summary.total_quoted_value || 0)}</div>
          <div className="text-[11px] text-[#6D8E87] mt-1">total quotations sent</div>
        </div>
        <div className="medical-card p-4 border-l-4 border-l-emerald-500" data-testid="team-summary-approved">
          <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold">Approved (Pipeline)</div>
          <div className="text-2xl md:text-[1.6rem] font-black text-emerald-700" style={{ fontFamily: 'Outfit' }}>{formatINR(summary.total_approved_value || 0)}</div>
          <div className="text-[11px] text-[#6D8E87] mt-1">approved quotes · awaiting Won</div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="medical-card p-4">
          <div className="text-xs text-[#6D8E87]">Revenue Won</div>
          <div className="text-xl font-bold text-[#00a69c]" style={{ fontFamily: 'Outfit' }}>{formatINR(summary.total_revenue)}</div>
        </div>
        <div className="medical-card p-4">
          <div className="text-xs text-[#6D8E87]">Avg Conversion</div>
          <div className="text-xl font-bold text-emerald-700" style={{ fontFamily: 'Outfit' }}>{summary.avg_conversion || 0}%</div>
        </div>
        <div className="medical-card p-4">
          <div className="text-xs text-[#6D8E87]">Active Leads</div>
          <div className="text-xl font-bold text-blue-700" style={{ fontFamily: 'Outfit' }}>{summary.total_active_leads || 0}</div>
        </div>
        <div className="medical-card p-4">
          <div className="text-xs text-[#6D8E87]">Total Leads</div>
          <div className="text-xl font-bold text-[#3B5A54]" style={{ fontFamily: 'Outfit' }}>{members.reduce((a, m) => a + (m.total_leads || 0), 0)}</div>
        </div>
      </div>

      {/* Role filter tabs */}
      {availableRoles.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setRoleFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
              roleFilter === 'all' ? 'bg-[#00a69c] text-white' : 'bg-[#F4F9F7] text-[#3B5A54] hover:bg-[#E2F3F0]'
            }`}
            data-testid="role-filter-all"
          >All ({members.length})</button>
          {availableRoles.map(r => (
            <button
              key={r}
              onClick={() => setRoleFilter(r)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                roleFilter === r ? 'bg-[#00a69c] text-white' : 'bg-[#F4F9F7] text-[#3B5A54] hover:bg-[#E2F3F0]'
              }`}
              data-testid={`role-filter-${r.replace(/\s+/g, '-')}`}
            >{r} ({members.filter(m => m.role === r).length})</button>
          ))}
        </div>
      )}

      <PipelineToolbar
        pageKey="team"
        sortBy={teamSortBy}
        onSortChange={setTeamSortBy}
        showWindowChips={false}
        showForecastToggle={false}
        sortOptions={[
          { key: 'pipeline_desc', label: 'Pipeline: High → Low' },
          { key: 'approved_desc', label: 'Approved: High → Low' },
          { key: 'value_asc',     label: 'Pipeline: Low → High' },
        ]}
      />

      {/* Member cards */}
      <div className="flex justify-end mb-3">
        <ViewToggle mode={viewMode} onChange={setViewMode} testIdPrefix="team-view" />
      </div>
      {viewMode === 'cards' && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-members-grid">
        {visibleMembers.length === 0 ? (
          <div className="col-span-full p-8 text-center text-[#6D8E87] medical-card">
            No team members in this view.
          </div>
        ) : visibleMembers.map(m => (
          <div
            key={m.user_id}
            role="button"
            tabIndex={0}
            onClick={() => openMember(m)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openMember(m); }}
            className="medical-card p-4 text-left hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer"
            data-testid={`member-card-${m.user_id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#E2F3F0] text-[#00a69c] flex items-center justify-center font-bold" style={{ fontFamily: 'Outfit' }}>
                  {(m.name || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-bold text-[#052A23]" data-testid={`member-name-${m.user_id}`}>{m.name}</div>
                  <div className="text-xs text-[#6D8E87]">{m.region || '—'}</div>
                </div>
              </div>
              <ArrowRight size={16} className="text-[#6D8E87]" />
            </div>
            <div className="mb-3">
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ROLE_COLOR[m.role] || 'bg-gray-100 text-gray-700'}`}>
                {m.role}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-[10px] text-[#6D8E87] uppercase">Revenue</div>
                <div className="text-sm font-bold text-[#00a69c]" data-testid={`member-revenue-${m.user_id}`}>{formatINR(m.revenue_won)}</div>
              </div>
              <div>
                <div className="text-[10px] text-[#6D8E87] uppercase">Conv</div>
                <div className="text-sm font-bold text-emerald-700">{m.conversion_rate}%</div>
              </div>
              <div>
                <div className="text-[10px] text-[#6D8E87] uppercase">Score</div>
                <div className="text-sm font-bold text-purple-700">{m.performance_score}</div>
              </div>
            </div>
            {/* Value rollups (Feb 2026) — pipeline is the leading indicator,
                quoted shows quotation activity, approved shows how much has
                progressed past the first gate. */}
            <div className="grid grid-cols-3 gap-2 text-center mt-2 pt-2 border-t border-[#D5E8E4]">
              <div>
                <div className="text-[10px] text-[#6D8E87] uppercase">Pipeline</div>
                <div className="text-xs font-bold text-slate-700" data-testid={`member-pipeline-${m.user_id}`}>{formatINR(m.pipeline_value || 0)}</div>
              </div>
              <div>
                <div className="text-[10px] text-[#6D8E87] uppercase">Quoted</div>
                <div className="text-xs font-bold text-amber-700" data-testid={`member-quoted-${m.user_id}`}>{formatINR(m.quoted_value || 0)}</div>
              </div>
              <div>
                <div className="text-[10px] text-[#6D8E87] uppercase">Approved</div>
                <div className="text-xs font-bold text-emerald-700" data-testid={`member-approved-${m.user_id}`}>{formatINR(m.approved_value || 0)}</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center mt-2 pt-2 border-t border-[#D5E8E4]">
              <div>
                <div className="text-[10px] text-[#6D8E87]">Leads</div>
                <div className="text-xs font-semibold text-[#052A23]">{m.leads_active}/{m.total_leads}</div>
              </div>
              <div>
                <div className="text-[10px] text-[#6D8E87]">Quotes</div>
                <div className="text-xs font-semibold text-[#052A23]">{m.quotes_approved}/{m.quotes_total}</div>
              </div>
              <div>
                <div className="text-[10px] text-[#6D8E87]">PI Open</div>
                <div className="text-xs font-semibold text-[#052A23]">{m.pis_open}</div>
              </div>
            </div>
            {/* Review reliability row (Phase 2 — Feb 2026). Only rendered when
                this member has at least one closed review — otherwise we hide
                the strip entirely to avoid noise for members who joined new
                cycles that haven't closed yet. */}
            {reliability[m.user_id] && reliability[m.user_id].review_count > 0 && (
              <div className="grid grid-cols-3 gap-2 text-center mt-2 pt-2 border-t border-[#D5E8E4]" data-testid={`member-reliability-${m.user_id}`}>
                <div>
                  <div className="text-[10px] text-amber-700 uppercase font-bold flex items-center justify-center gap-0.5">
                    <Target size={9} weight="fill" /> Achievement
                  </div>
                  <div className={`text-xs font-bold ${reliability[m.user_id].avg_achievement_pct >= 100 ? 'text-emerald-600' : reliability[m.user_id].avg_achievement_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`} title="Average won-against-target across closed reviews">
                    {reliability[m.user_id].avg_achievement_pct}%
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-purple-700 uppercase font-bold">Accuracy</div>
                  <div className="text-xs font-bold text-purple-700" title="How much of what they committed actually won">
                    {reliability[m.user_id].avg_accuracy_pct}%
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-[#6D8E87] uppercase">Reviews</div>
                  <div className="text-xs font-semibold text-[#052A23]">
                    {reliability[m.user_id].review_count}
                    {reliability[m.user_id].inadequate_count > 0 && (
                      <span className="ml-1 text-[9px] text-amber-700" title={`${reliability[m.user_id].inadequate_count} inadequate commitment(s)`}>
                        ⚠{reliability[m.user_id].inadequate_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {isPresident && m.user_id !== user?.id && m.role !== 'President' && (
              <div className="mt-3 pt-3 border-t border-[#D5E8E4]" onClick={(e) => e.stopPropagation()}>
                <div className="flex gap-2 mb-1.5">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openEditModal(m); }}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-[#00a69c] text-[#00a69c] rounded text-xs font-medium hover:bg-[#E2F3F0] transition"
                    data-testid={`edit-member-${m.user_id}`}
                  >
                    <PencilSimple size={12} /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeMember(m); }}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-rose-300 text-rose-600 rounded text-xs font-medium hover:bg-rose-50 transition"
                    data-testid={`delete-member-${m.user_id}`}
                  >
                    <Trash size={12} /> Remove
                  </button>
                </div>
                {/* Feb 2026 — Session controls for President. Both actions
                    bump the target's token_version so every existing JWT dies
                    instantly. Force-Logout keeps the account active; Reset
                    Password additionally swaps the credential. */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleForceLogout(m); }}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-amber-300 text-amber-700 rounded text-[11px] font-medium hover:bg-amber-50 transition"
                    data-testid={`force-logout-${m.user_id}`}
                    title="Kick this user off every device. Their next click will send them back to the login page."
                  >
                    Force Logout
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleAdminResetPassword(m); }}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-slate-300 text-slate-700 rounded text-[11px] font-medium hover:bg-slate-50 transition"
                    data-testid={`reset-password-${m.user_id}`}
                    title="Set a new password for this user. Also signs them out of every device."
                  >
                    Reset Password
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      )}

      {viewMode === 'list' && (
        <div>
          <PowerTable
            testIdPrefix="team-table"
            initialSort={{ key: 'won_value', dir: 'desc' }}
            onRowClick={(row) => openMember(row)}
            rows={visibleMembers.map(m => ({ ...m, id: m.user_id }))}
            emptyLabel="No team members in this view."
            columns={[
              { key: 'name', label: 'Member', sortable: true, cell: (r) => (
                <div>
                  <div className="font-semibold text-[#052A23]">{r.name}</div>
                  <div className="text-[11px] text-[#6D8E87]">{r.email || '—'}</div>
                </div>
              ) },
              { key: 'role', label: 'Role', sortable: true },
              { key: 'region', label: 'Region', sortable: true, cell: (r) => r.region || '—' },
              { key: 'leads_active', label: 'Active Leads', sortable: true, className: 'text-right',
                cell: (r) => `${r.leads_active || 0} / ${r.total_leads || 0}` },
              { key: 'pipeline_value', label: 'Pipeline', sortable: true, className: 'text-right',
                accessor: (r) => Number(r.pipeline_value || 0),
                cell: (r) => <span className="font-semibold text-slate-600">{formatINR(r.pipeline_value || 0)}</span> },
              { key: 'quoted_value', label: 'Quoted', sortable: true, className: 'text-right',
                accessor: (r) => Number(r.quoted_value || 0),
                cell: (r) => <span className="font-semibold text-amber-700">{formatINR(r.quoted_value || 0)}</span> },
              { key: 'approved_value', label: 'Approved', sortable: true, className: 'text-right',
                accessor: (r) => Number(r.approved_value || 0),
                cell: (r) => <span className="font-semibold text-emerald-700">{formatINR(r.approved_value || 0)}</span> },
              { key: 'won_value', label: 'Won', sortable: true, className: 'text-right',
                accessor: (r) => Number(r.won_value || 0),
                cell: (r) => <span className="font-bold text-[#00a69c]">{formatINR(r.won_value || 0)}</span> },
            ]}
          />
        </div>
      )}

      {/* Detail loading overlay */}
      {selectedMember && detailLoading && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 text-[#00a69c]">Loading {selectedMember.name}...</div>
        </div>
      )}

      {/* Add / Edit Member Modal — President only */}
      <Dialog open={!!modalMode} onOpenChange={(o) => { if (!o) { setModalMode(null); setMemberForm(emptyMember); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
              {modalMode === 'add' ? 'Add Team Member' : 'Edit Team Member'}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="team-member-form">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">Full Name *</label>
              <input type="text" value={memberForm.name}
                onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                placeholder="e.g., Rajesh Kumar" data-testid="member-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">Email *</label>
              <input type="email" value={memberForm.email}
                onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                placeholder="user@company.com" data-testid="member-email-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">Employee ID *</label>
              <input type="text" value={memberForm.employee_id}
                onChange={(e) => setMemberForm({ ...memberForm, employee_id: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                placeholder="EMP001" data-testid="member-empid-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">Role *</label>
              <select value={memberForm.role}
                onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none bg-white"
                data-testid="member-role-input">
                {ALL_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">Region</label>
              <select value={memberForm.region}
                onChange={(e) => setMemberForm({ ...memberForm, region: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none bg-white"
                data-testid="member-region-input">
                <option value="">— Not regional —</option>
                {ALL_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">Phone</label>
              <input type="text" value={memberForm.phone}
                onChange={(e) => setMemberForm({ ...memberForm, phone: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                placeholder="98XXXXXXXX" data-testid="member-phone-input" />
            </div>
            {modalMode === 'add' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-[#3B5A54] mb-1">Aadhar Number</label>
                  <input type="text" value={memberForm.aadhar_number}
                    onChange={(e) => setMemberForm({ ...memberForm, aadhar_number: e.target.value })}
                    className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    placeholder="12-digit (optional)" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#3B5A54] mb-1">Date of Joining</label>
                  <input type="date" value={memberForm.date_of_joining}
                    onChange={(e) => setMemberForm({ ...memberForm, date_of_joining: e.target.value })}
                    className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#3B5A54] mb-1">Monthly Salary (₹, optional)</label>
                  <input type="number" min="0" value={memberForm.monthly_salary}
                    onChange={(e) => setMemberForm({ ...memberForm, monthly_salary: e.target.value })}
                    className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    placeholder="Used for Profitability, VP+ only" data-testid="member-salary-input" />
                </div>
              </>
            )}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">
                {modalMode === 'add' ? 'Initial Password *' : 'Reset Password (optional)'}
              </label>
              <input type="text" value={memberForm.password}
                onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none font-mono"
                placeholder={modalMode === 'add' ? 'At least 8 characters' : 'Leave blank to keep existing'}
                data-testid="member-password-input" />
              <p className="text-[10px] text-[#6D8E87] mt-1">Share with the user securely. They can change it from their profile.</p>
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2 mt-2">
              <button onClick={() => { setModalMode(null); setMemberForm(emptyMember); }}
                className="px-4 py-2 border border-[#D5E8E4] rounded-lg text-sm text-[#3B5A54] hover:bg-[#F4F9F7]"
                data-testid="cancel-member-btn">Cancel</button>
              <button onClick={submitMember} disabled={memberFormBusy}
                className="px-4 py-2 bg-[#00a69c] text-white rounded-lg text-sm font-medium hover:bg-[#008f86] disabled:opacity-50"
                data-testid="save-member-btn">
                {memberFormBusy ? 'Saving...' : (modalMode === 'add' ? 'Create Member' : 'Save Changes')}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteUserModal
        open={!!deleteTarget}
        target={deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteWithReason}
      />
    </div>
  );
};

export default Team;
